import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import LocationForm, { LocationFormValues } from './LocationForm';
import { useCreateLocation } from '../../hooks/useCreateLocation';

const CreateLocationModal = forwardRef<ModalCenterRef>((_, ref) => {
  const { t } = useTranslation('locationsModule');
  const { toast } = useToast();
  const createLocationMutation = useCreateLocation();

  const modalRef = ref as React.RefObject<ModalCenterRef>;

  const handleCancel = () => {
    modalRef?.current?.closeModal();
  };

  const handleSubmit = async (values: LocationFormValues) => {
    try {
      await createLocationMutation.mutateAsync({
        code: values.code.trim(),
        type_id: values.type_id,
        rack_id: values.rack_id === -1 ? null : values.rack_id,
        shelf: values.shelf,
        slot: values.slot,
      });

      toast({
        title: t('modal.create.success_title'),
        description: t('modal.create.success_description'),
      });

      modalRef?.current?.closeModal();
    } catch (error: any) {
      toast({
        title: t('modal.create.error_title'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t('modal.create.title')}
      type="default"
      width="lg"
      className="h-[calc(100%-30px)] sm:h-auto"
    >
      <LocationForm
        defaultValues={{ warehouse_id: 1 }}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        submitLabel={t('button.create')}
        isSubmitting={createLocationMutation.isPending}
      />
    </ModalCenter>
  );
});

CreateLocationModal.displayName = 'CreateLocationModal';

export default CreateLocationModal;
